import { useState, useEffect, useCallback, useMemo } from 'react';
import { KstDataGrid } from '@/components/KstDataGrid';
import { api } from '@/services/api-class';
import { hasValue } from '@/utils/legacy-utils';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getSubmoduleOrKerktonRoleFromReportAuditLogEntry = _wg('getSubmoduleOrKerktonRoleFromReportAuditLogEntry');
const callCardAndReportAuditLogEntryTemplate = _wg('callCardAndReportAuditLogEntryTemplate');

interface CaseReportAuditLogProps {
    reportId: number | null;
    title?: string;
}

export function CaseReportAuditLog({
    reportId = null,
    title = 'Audit Log',
}: CaseReportAuditLogProps) {
    const [entries, setEntries] = useState<any[] | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(20);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const formatDateTime = useCallback((dateStr: string): string => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        const month = (date.getMonth() + 1);
        const day = date.getDate();
        const year = date.getFullYear();
        let hours = date.getHours();
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;
        return (month < 10 ? '0' : '') + month + '/' +
               (day < 10 ? '0' : '') + day + '/' +
               year + ' ' +
               hours + ':' +
               (minutes < 10 ? '0' : '') + minutes + ':' +
               (seconds < 10 ? '0' : '') + seconds + ' ' + ampm;
    }, []);

    const getRole = useCallback((entry: any): string => {
        return getSubmoduleOrKerktonRoleFromReportAuditLogEntry(entry);
    }, []);

    const formatEntry = useCallback((entry: string): string => {
        if (!entry) return '';
        if (typeof (window as any).callCardAndReportAuditLogEntryTemplate === 'function') {
            return callCardAndReportAuditLogEntryTemplate(entry);
        }
        return entry;
    }, []);

    const getActionClass = useCallback((action: string): string => {
        switch (action) {
            case 'CREATE': return 'audit-action-create';
            case 'UPDATE': return 'audit-action-update';
            case 'STATUS_CHANGE': return 'audit-action-status';
            case 'DELETE': return 'audit-action-delete';
            case 'Viewed': return 'audit-action-view';
            default: return '';
        }
    }, []);

    const gridColumns = useMemo(() => {
        return [
            { field: 'formattedDateTime', name: 'Date/Time', size: '160px',
              template: (e: any) => formatDateTime(e.dateCreated) },
            { field: 'loggedByFirstName', name: 'First', size: '100px' },
            { field: 'loggedByLastName', name: 'Last', size: '100px' },
            { field: 'role', name: 'Role', size: '100px',
              template: (e: any) => getRole(e) },
            { field: 'action', name: 'Action', size: '120px',
              template: (e: any) => `<span class="audit-action-badge ${getActionClass(e.action)}">${e.action}</span>` },
            { field: 'entry', name: 'Entry',
              template: (e: any) => formatEntry(e.entry) }
        ];
    }, [formatDateTime, getRole, getActionClass, formatEntry]);

    const gridRows = useMemo(() => {
        return {
            entries: entries === null ? null : entries.map((e: any) => ({
                ...e,
                formattedDateTime: formatDateTime(e.dateCreated)
            }))
        };
    }, [entries, formatDateTime]);

    const gridPager = useMemo(() => {
        return {
            curPage: currentPage,
            numPages: totalPages,
            records: totalRecords,
            pageSize: pageSize,
            isVisible: totalPages > 1
        };
    }, [currentPage, totalPages, totalRecords, pageSize]);

    const loadAuditLog = useCallback((page: number) => {
        if (!hasValue(reportId)) {
            setEntries([]);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        const operation = {
            data: {
                page: page,
                take: pageSize,
                skip: (page - 1) * pageSize
            }
        };

        api.getAuditLogEntries(
            reportId,
            page,
            String(pageSize),
            JSON.stringify(operation),
            (result: any) => {
                setEntries(result.entries || []);
                const records = result.records || 0;
                setTotalRecords(records);
                setTotalPages(Math.ceil(records / pageSize) || 1);
                setIsLoading(false);
            }
        );
    }, [reportId, pageSize]);

    const goToPage = useCallback((page: number) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
        loadAuditLog(page);
    }, [totalPages, loadAuditLog]);

    // Watch reportId
    useEffect(() => {
        if (hasValue(reportId)) {
            setCurrentPage(1);
            loadAuditLog(1);
        }
    }, [reportId, loadAuditLog]);

    return (
        <div>
            <KstDataGrid
                columns={gridColumns}
                rows={gridRows}
                pager={gridPager}
                loading={isLoading}
                showcheckbox={false}
                title={title}
                mobiletitlefield="formattedDateTime"
                onGoToPage={goToPage}
            />
        </div>
    );
}

(window as any).CaseReportAuditLogReact = CaseReportAuditLog;
